/**
 * Long Form AI — the narrative contract.
 *
 * This is the shape the V0 server-side stub emits over the WebSocket and, later, the shape
 * Data Science produces (a producer-only swap — see openspec `long-form-ai`, tasks 1.1d / 1.5).
 *
 * Two properties are DERIVED here and never transmitted: whether a beat is skippable and
 * whether it is required. Both follow from `segments` — see `beatHasVerbatim` below. Keeping
 * the compliance-bearing "required" out of the payload keeps it out of DS's hands (task 1.1d).
 */

/** A run of narrative text — `verbatim` is locked copy the expert must deliver word-for-word;
 *  `generated` is paraphrasable connective language. */
export type NarrativeSegment = { kind: "verbatim"; text: string } | { kind: "generated"; text: string };

export interface NarrativeBeat {
    /**
     * Stable identity that survives a content swap. A replacement narrative preserves each
     * beat's covered/skipped state by matching on this id, so it must NOT be the index
     * (the beat count can change) — see task 1.21.
     */
    id: string;
    /** 1-based position within its step, e.g. the `3` in the `Offer | 3/5` badge. */
    index: number;
    /** Total beats in this beat's step, e.g. the `5` in `Offer | 3/5`. */
    of: number;
    segments: NarrativeSegment[];
    /**
     * How the card lays out `segments`. "inline" (default) flows them as one paragraph with verbatim
     * runs highlighted; "bullets" renders each segment as its own bullet — used for a single-beat
     * Offer that lists its statements. Multi-beat (multi-step) offers stay inline per beat.
     */
    layout?: "inline" | "bullets";
}

/**
 * The funnel scaffold — Transition → Guide → Offer → Close → Enroll. Modelled as DATA,
 * not literals, so a differently shaped event can be honoured later (task 1.18). `guide` and `enroll`
 * are the two alternative enrollment paths and are mutually exclusive: guided browsing on keeps
 * `guide` and drops `enroll`; off keeps `enroll` and drops `guide`. Only `offer` carries multiple
 * beats; the others are single-card steps.
 */
export type NarrativeStepKind = "transition" | "guide" | "offer" | "close" | "enroll";

export interface NarrativeStep {
    kind: NarrativeStepKind;
    /** Rail label, e.g. "Transition", "Guide", "Offer", "Close", "Enroll". */
    label: string;
    beats: NarrativeBeat[];
}

export interface Narrative {
    steps: NarrativeStep[];
}

/** Per-beat progress. Session-scoped, keyed by beat id (tasks 1.21 / 5.5). */
export type BeatStatus = "pending" | "covered" | "skipped";

// ---- Derived, never transmitted ----

/** A beat carrying ANY verbatim segment must be read exactly ⇒ it is required and not skippable. */
export const beatHasVerbatim = (beat: NarrativeBeat): boolean =>
    beat.segments.some((segment) => segment.kind === "verbatim");

/** Skippable iff the beat has no verbatim content (task 1.1d). */
export const isBeatSkippable = (beat: NarrativeBeat): boolean => !beatHasVerbatim(beat);

const sameSegment = (a: NarrativeSegment, b: NarrativeSegment): boolean => a.kind === b.kind && a.text === b.text;

const sameBeat = (a: NarrativeBeat, b: NarrativeBeat): boolean =>
    a.id === b.id &&
    a.index === b.index &&
    a.of === b.of &&
    a.layout === b.layout &&
    a.segments.length === b.segments.length &&
    a.segments.every((segment, i) => {
        const other = b.segments[i];
        return other !== undefined && sameSegment(segment, other);
    });

const sameStep = (a: NarrativeStep, b: NarrativeStep): boolean =>
    a.kind === b.kind &&
    a.label === b.label &&
    a.beats.length === b.beats.length &&
    a.beats.every((beat, i) => {
        const other = b.beats[i];
        return other !== undefined && sameBeat(beat, other);
    });

/**
 * Structural identity — same steps, same beats, same copy. The bus is Kafka-backed and at-least-once,
 * so the same producer message can arrive twice; without this, a redelivery raises "New script
 * available" for content the expert is already reading. Compares everything that decides what the
 * expert SEES: `index`/`of` drive the beat badge and `layout` changes the card, so both count.
 */
export const isSameNarrative = (a: Narrative, b: Narrative): boolean =>
    a === b ||
    (a.steps.length === b.steps.length &&
        a.steps.every((step, i) => {
            const other = b.steps[i];
            return other !== undefined && sameStep(step, other);
        }));

/** A beat carrying ANY generated segment has cue-derived prose ⇒ its "I heard <cue>" provenance is
 *  real. A pure-verbatim beat (100% locked copy) has none, so it must NOT claim cue provenance. */
export const beatHasGenerated = (beat: NarrativeBeat): boolean =>
    beat.segments.some((segment) => segment.kind === "generated");
